import React, { useState, useRef } from 'react';
import { extractInvoiceData, ExtractedInvoiceItem } from '@/services/aiExtraction';
import {
  parsePurchaseSpreadsheet,
  expandLaptopUnits,
  ExtractedSpreadsheetPurchase,
  ExtractedSpreadsheetItem,
} from '@/services/purchaseSpreadsheet';
import { downloadPurchaseImportTemplate } from '@/lib/purchaseImportTemplate';
import {
  groupPurchaseReviewItems, groupPurchaseReviewModels, getReviewLaptopSpecs, isReviewLaptop,
  isMissingLaptopSpec, updatePurchaseReviewGroup, consolidateReviewedAccessories,
  type PurchaseReviewGroup, type ReviewGroupField,
} from '@/services/purchaseReview';
import { useAuth } from '@/context/AuthContext';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  Sparkles,
  Upload,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  Download,
  Edit2,
  ChevronRight,
  Info,
  Plus,
  Trash2,
  Layers,
} from 'lucide-react';

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

export interface ExtractedReviewItem {
  id: string;
  description: string;
  quantity: number;
  unit_cost: number;
  draftItem?: ExtractedSpreadsheetItem;
  manualType?: 'laptop' | 'accessory' | '';
  manualBrand?: string;
  manualModel?: string;
  ram?: string;
  storage?: string;
  reviewSeparately?: boolean;
}

export interface InvoiceExtractPreFill {
  invoice_reference: string;
  purchase_date: string;
  shipping_cost: number;
  other_cost: number;
  discount: number;
  amount_paid: number;
  payment_method: 'cash' | 'bank_transfer' | 'mobile_wallet' | 'other';
  notes: string;
  extracted_supplier_name: string | null;
  extracted_items: ExtractedInvoiceItem[];
  extracted_draft?: ExtractedSpreadsheetPurchase | null;
}

interface InvoiceExtractorModalProps {
  onConfirm: (preFill: InvoiceExtractPreFill) => void;
  onCancel: () => void;
}

export const InvoiceExtractorModal: React.FC<InvoiceExtractorModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  const { role } = useAuth();
  const isOwner = role === 'owner';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const spreadsheetInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<'invoice' | 'spreadsheet'>('invoice');
  const [step, setStep] = useState<'upload' | 'extracting' | 'review' | 'not_configured'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  // Editable extracted header fields
  const [editSupplierName, setEditSupplierName] = useState('');
  const [editReference, setEditReference] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editShipping, setEditShipping] = useState(0);
  const [editOther, setEditOther] = useState(0);
  const [editDiscount, setEditDiscount] = useState(0);
  const [editAmountPaid, setEditAmountPaid] = useState(0);
  const [editPaymentMethod, setEditPaymentMethod] = useState<'cash' | 'bank_transfer' | 'mobile_wallet' | 'other'>('cash');
  const [editNotes, setEditNotes] = useState('');

  // Editable extracted items (structured review items with stable IDs)
  const [editItems, setEditItems] = useState<ExtractedReviewItem[]>([]);
  const [spreadsheetWarnings, setSpreadsheetWarnings] = useState<string[]>([]);
  const [extractionMethod, setExtractionMethod] = useState<string | null>(null);
  const [spreadsheetDraft, setSpreadsheetDraft] = useState<ExtractedSpreadsheetPurchase | null>(null);

  // ── File Handlers ─────────────────────────────────────────────────────────

  const handleInvoiceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setExtractError(null);

    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleSpreadsheetFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(null);
    setExtractError(null);
  };

  const handleExtractInvoice = async () => {
    if (!selectedFile) return;
    setStep('extracting');
    setExtractError(null);
    setSpreadsheetDraft(null);

    const result = await extractInvoiceData(selectedFile);

    if (result.notConfigured) {
      setStep('not_configured');
      return;
    }

    if (result.error || !result.data) {
      setStep('upload');
      setExtractError(result.error ?? 'Extraction failed');
      return;
    }

    const data = result.data;
    setEditSupplierName(data.supplier_name ?? '');
    setEditReference(data.invoice_reference ?? '');
    setEditDate(data.invoice_date ?? new Date().toISOString().slice(0, 10));
    setEditShipping(data.shipping_cost ?? 0);
    setEditOther(data.other_cost ?? 0);
    setEditDiscount(data.discount ?? 0);
    setEditAmountPaid(0);
    setEditPaymentMethod('cash');
    setEditNotes(data.notes ?? '');

    const reviewItems: ExtractedReviewItem[] =
      data.items.length > 0
        ? data.items.map((item, idx) => ({
            id: `inv-${idx + 1}`,
            description: item.description,
            quantity: item.quantity ?? 1,
            unit_cost: item.unit_cost ?? 0,
          }))
        : [{ id: 'inv-1', description: '', quantity: 1, unit_cost: 0 }];

    setEditItems(reviewItems);
    setSpreadsheetWarnings([]);
    setExtractionMethod('invoice_image_or_pdf');
    setStep('review');
  };

  const handleExtractSpreadsheet = async () => {
    if (!selectedFile) return;
    if (!isOwner) {
      setExtractError('Only the owner has permission to import purchase spreadsheets.');
      return;
    }
    setStep('extracting');
    setExtractError(null);

    const result = await parsePurchaseSpreadsheet(selectedFile);

    if (result.notConfigured) {
      setStep('not_configured');
      return;
    }

    if (result.error || !result.data) {
      setStep('upload');
      setExtractError(result.error ?? 'Spreadsheet import failed');
      return;
    }

    const data = result.data;
    setSpreadsheetDraft(data);
    setEditSupplierName(data.supplier_name ?? '');
    setEditReference(data.invoice_reference ?? '');
    setEditDate(data.invoice_date ?? new Date().toISOString().slice(0, 10));
    setEditShipping(data.shipping_cost ?? 0);
    setEditOther(data.other_cost ?? 0);
    setEditDiscount(data.discount ?? 0);
    setEditAmountPaid(data.amount_paid ?? 0);
    setEditPaymentMethod(data.payment_method ?? 'cash');
    setEditNotes(data.notes ?? '');
    setSpreadsheetWarnings(data.warnings ?? []);
    setExtractionMethod(data.extraction_method);

    const reviewItems: ExtractedReviewItem[] =
      data.items.length > 0
        ? data.items.map((item) => {
            let desc = item.original_description;
            if (!desc && item.brand && item.model) {
              desc = `${item.brand} ${item.model}`;
            }
            return {
              id: item.id,
              description: desc || 'Item',
              quantity: item.quantity ?? 1,
              unit_cost: item.unit_cost ?? 0,
              draftItem: item,
            };
          })
        : [{ id: 'sheet-1', description: '', quantity: 1, unit_cost: 0 }];

    setEditItems(reviewItems);
    setStep('review');
  };

  const handleConfirm = () => {
    let finalDraft: ExtractedSpreadsheetPurchase | null = null;
    if (spreadsheetDraft) {
      const finalItems: ExtractedSpreadsheetItem[] = [];

      setExtractError(null);
      try {
        // Validate the complete edited draft before allocating physical units.
        let totalQuantity = 0;
        for (const item of editItems) {
          const quantity = Number(item.quantity);
          if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 2000) {
            throw new Error(`"${item.description || 'Item'}": quantity must be a whole number from 1 to 2000.`);
          }
          totalQuantity += quantity;
          if (totalQuantity > 2000) throw new Error('The reviewed purchase exceeds the 2000-unit limit.');
          if (!Number.isFinite(Number(item.unit_cost)) || Number(item.unit_cost) < 0) {
            throw new Error(`"${item.description || 'Item'}": enter a valid non-negative unit cost.`);
          }
        }

        for (const item of editItems) {
          let draftItem = item.draftItem;
          if (!draftItem || draftItem.item_type === 'unknown') {
            if (!item.manualType) throw new Error(`"${item.description || 'Item'}": choose Laptop or Accessory as the item type.`);
            if (!item.description.trim()) throw new Error('Enter a description for each added item, or remove its row.');
            if (item.manualType === 'laptop' && (!item.manualBrand?.trim() || !item.manualModel?.trim())) {
              throw new Error(`"${item.description}": enter the laptop brand and model.`);
            }
            draftItem = {
              ...draftItem,
              id: item.id,
              item_type: item.manualType,
              source_sheet: draftItem?.source_sheet || 'Manual',
              source_row: draftItem?.source_row || 0,
              original_description: item.description.trim(),
              brand: item.manualType === 'laptop' ? item.manualBrand?.trim() : undefined,
              model: item.manualType === 'laptop' ? item.manualModel?.trim() : undefined,
              accessory_name: item.manualType === 'accessory' ? item.description.trim() : undefined,
              accessory_category: item.manualType === 'accessory' ? 'Other Accessories' : undefined,
              unit_cost: Number(item.unit_cost),
              warnings: draftItem?.warnings || [],
              is_valid: true,
            };
          }
          const reviewedItem: ExtractedSpreadsheetItem = {
            ...draftItem,
            original_description: item.description,
            quantity: Number(item.quantity),
            unit_cost: Number(item.unit_cost),
            ...getReviewLaptopSpecs(item),
          };
          if (reviewedItem.item_type === 'laptop') {
            const missing = [
              isMissingLaptopSpec(reviewedItem.ram) ? 'RAM' : '',
              isMissingLaptopSpec(reviewedItem.storage) ? 'SSD / storage' : '',
            ].filter(Boolean);
            if (missing.length) {
              throw new Error(`"${item.description}": enter ${missing.join(' and ')} before continuing. If a component is absent, explicitly choose No RAM installed or No drive installed.`);
            }
            // updateItem already updates draftItem.quantity; always normalize final values.
            finalItems.push(...expandLaptopUnits(reviewedItem, Number(item.quantity)));
          } else if (reviewedItem.item_type === 'accessory' || reviewedItem.item_type === 'new_accessory') {
            finalItems.push({ ...reviewedItem, item_type: 'accessory' });
          } else {
            throw new Error(`"${item.description}": resolve the item type before continuing.`);
          }
        }
      } catch (error) {
        setExtractError(error instanceof Error ? error.message : 'Please correct the reviewed items.');
        return;
      }

      finalDraft = {
        ...spreadsheetDraft,
        supplier_name: editSupplierName.trim() || null,
        invoice_reference: editReference.trim() || null,
        invoice_date: editDate || new Date().toISOString().slice(0, 10),
        shipping_cost: Number(editShipping) || 0,
        other_cost: Number(editOther) || 0,
        discount: Number(editDiscount) || 0,
        amount_paid: Number(editAmountPaid) || 0,
        payment_method: editPaymentMethod,
        notes: editNotes.trim() || null,
        items: consolidateReviewedAccessories(finalItems),
      };
    }

    onConfirm({
      invoice_reference: editReference.trim(),
      purchase_date: editDate || new Date().toISOString().slice(0, 10),
      shipping_cost: Number(editShipping) || 0,
      other_cost: Number(editOther) || 0,
      discount: Number(editDiscount) || 0,
      amount_paid: Number(editAmountPaid) || 0,
      payment_method: editPaymentMethod,
      notes: editNotes.trim(),
      extracted_supplier_name: editSupplierName.trim() || null,
      extracted_items: editItems
        .filter((i) => i.description.trim())
        .map((i) => ({ description: i.description, quantity: i.quantity, unit_cost: i.unit_cost })),
      extracted_draft: finalDraft,
    });
  };

  const updateItem = (id: string, field: 'description' | 'quantity' | 'unit_cost', value: string | number) => {
    setEditItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        if (updated.draftItem) {
          updated.draftItem = {
            ...updated.draftItem,
            original_description: field === 'description' ? String(value) : updated.draftItem.original_description,
            quantity: field === 'quantity' ? Number(value) : updated.draftItem.quantity,
            unit_cost: field === 'unit_cost' ? Number(value) : updated.draftItem.unit_cost,
          };
        }
        return updated;
      })
    );
  };

  const updateManualItem = (
    id: string,
    changes: Partial<Pick<ExtractedReviewItem, 'manualType' | 'manualBrand' | 'manualModel'>>
  ) => setEditItems((prev) => prev.map((item) => item.id === id ? { ...item, ...changes } : item));

  const removeItem = (id: string) => {
    setEditItems((prev) => prev.filter((item) => item.id !== id));
  };

  const addItem = () => {
    const newId = `manual-item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setEditItems((prev) => [...prev, { id: newId, description: '', quantity: 1, unit_cost: 0 }]);
  };

  const reviewGroups = groupPurchaseReviewItems(editItems);
  const reviewModels = groupPurchaseReviewModels(reviewGroups);
  const missingSpecsGroups = spreadsheetDraft ? reviewGroups.filter(({ item }) => {
    const specs = getReviewLaptopSpecs(item);
    return isReviewLaptop(item) && (isMissingLaptopSpec(specs.ram) || isMissingLaptopSpec(specs.storage));
  }) : [];
  const updateGroup = (group: PurchaseReviewGroup, field: ReviewGroupField, value: string | number) => {
    try {
      if (group.members.length === 1 && field !== 'ram' && field !== 'storage') {
        updateItem(group.item.id, field, value);
      } else {
        setEditItems(updatePurchaseReviewGroup(editItems, group.members.map(item => item.id), field, value));
      }
      setExtractError(null);
      return true;
    } catch (error) {
      setExtractError(error instanceof Error ? error.message : 'Please check the group quantity.');
      return false;
    }
  };
  const removeGroup = (group: PurchaseReviewGroup) => {
    const ids = new Set(group.members.map(item => item.id));
    setEditItems(prev => prev.filter(item => !ids.has(item.id)));
  };
  const separateItem = (id: string) => {
    setEditItems(prev => prev.map(item => item.id === id ? { ...item, reviewSeparately: true } : item));
  };

  const inputClass =
    'w-full px-2 py-1.5 bg-white border border-brand-border rounded text-xs text-brand-black focus:outline-none focus:ring-1 focus:ring-brand-green';

  // Live Calculations for Review
  const subtotal = editItems.reduce((sum, item) => sum + (Number(item.quantity) || 1) * (Number(item.unit_cost) || 0), 0);
  const extraCosts = Number(editShipping) + Number(editOther);
  const total = Math.max(0, subtotal + extraCosts - Number(editDiscount));

  // ── Not Configured State ──────────────────────────────────────────────────
  if (step === 'not_configured') {
    return (
      <div className="p-6 flex flex-col items-center text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-amber-500" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-brand-black">AI Service Not Configured</h3>
          <p className="text-xs text-brand-muted mt-1 max-w-xs">
            The <strong>GEMINI_API_KEY</strong> secret has not been configured on the server.
          </p>
        </div>
        <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 text-left space-y-1">
          <p className="font-bold">To enable AI Extraction:</p>
          <p>1. Go to Supabase → Edge Functions → Secrets</p>
          <p>
            2. Add secret:{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">GEMINI_API_KEY</code> = your Google AI API key
          </p>
          <p>
            3. Deploy:{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">supabase functions deploy extract-purchase-spreadsheet</code>
          </p>
        </div>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover text-xs"
        >
          Close
        </button>
      </div>
    );
  }

  // ── Upload Step ───────────────────────────────────────────────────────────
  if (step === 'upload' || step === 'extracting') {
    return (
      <div className="space-y-4 text-xs">
        {/* Navigation Tabs */}
        <div className="flex items-center justify-between border-b border-brand-border pb-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => {
                setActiveTab('invoice');
                setSelectedFile(null);
                setExtractError(null);
              }}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                activeTab === 'invoice'
                  ? 'bg-brand-green text-white shadow-sm'
                  : 'text-brand-muted hover:text-brand-black hover:bg-brand-bg'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Scan Invoice Image or PDF</span>
            </button>

            {isOwner && (
              <button
                type="button"
                onClick={() => {
                  setActiveTab('spreadsheet');
                  setSelectedFile(null);
                  setExtractError(null);
                }}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  activeTab === 'spreadsheet'
                    ? 'bg-brand-green text-white shadow-sm'
                    : 'text-brand-muted hover:text-brand-black hover:bg-brand-bg'
                }`}
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                <span>Import Purchase Spreadsheet</span>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => { void downloadPurchaseImportTemplate(); }}
            className="flex items-center space-x-1 px-2.5 py-1 text-[11px] font-medium text-brand-green border border-brand-green/40 rounded hover:bg-green-50 transition-colors"
            title="Download canonical purchase spreadsheet template (.xlsx)"
          >
            <Download className="w-3 h-3" />
            <span>Download Template</span>
          </button>
        </div>

        {/* Security / Safety notice */}
        <div className="flex items-start space-x-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-800">
          <Info className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
          <span>
            <strong>Zero Automatic Database Writes.</strong> AI processes the document structure for your review.
            Stock and accounting records change only when you confirm and receive the final purchase draft.
          </span>
        </div>

        {extractError && <ErrorAlert message={extractError} />}

        {/* INVOICE TAB */}
        {activeTab === 'invoice' && (
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              selectedFile ? 'border-brand-green bg-green-50/40' : 'border-brand-border hover:border-brand-green hover:bg-green-50/20'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              onChange={handleInvoiceFileChange}
              className="hidden"
            />

            {previewUrl ? (
              <img src={previewUrl} alt="Invoice preview" className="max-h-48 mx-auto object-contain rounded" />
            ) : (
              <div className="flex flex-col items-center space-y-2 text-brand-muted">
                <Upload className="w-10 h-10 opacity-40" />
                <p className="font-semibold text-brand-black">Click to upload supplier invoice image or PDF</p>
                <p className="text-[11px]">JPEG, PNG, WEBP, HEIC, or PDF — max 20 MB</p>
              </div>
            )}

            {selectedFile && !previewUrl && (
              <div className="flex items-center justify-center space-x-2 text-brand-green font-semibold">
                <FileText className="w-4 h-4" />
                <span>{selectedFile.name}</span>
              </div>
            )}
          </div>
        )}

        {/* SPREADSHEET TAB */}
        {activeTab === 'spreadsheet' && isOwner && (
          <div
            onClick={() => spreadsheetInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              selectedFile ? 'border-brand-green bg-green-50/40' : 'border-brand-border hover:border-brand-green hover:bg-green-50/20'
            }`}
          >
            <input
              ref={spreadsheetInputRef}
              type="file"
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              onChange={handleSpreadsheetFileChange}
              className="hidden"
            />

            <div className="flex flex-col items-center space-y-2 text-brand-muted">
              <FileSpreadsheet className="w-10 h-10 text-brand-green opacity-80" />
              <p className="font-semibold text-brand-black">Upload Supplier Purchase Spreadsheet</p>
              <p className="text-[11px]">Excel (.xlsx) or CSV (.csv) — max 10 MB, up to 2,000 rows</p>
              <p className="text-[10px] text-brand-muted italic">Works with custom supplier sheets, varied columns, or our canonical template</p>
            </div>

            {selectedFile && (
              <div className="flex items-center justify-center space-x-2 text-brand-green font-semibold mt-3">
                <FileSpreadsheet className="w-4 h-4" />
                <span>{selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
              </div>
            )}
          </div>
        )}

        {selectedFile && (
          <p className="text-[11px] text-brand-muted text-center">
            Selected: <strong className="text-brand-black">{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
          </p>
        )}

        <div className="flex items-center justify-end space-x-3 pt-2 border-t border-brand-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={step === 'extracting'}
            className="px-3 py-2 text-brand-muted hover:text-brand-black font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedFile || step === 'extracting'}
            onClick={activeTab === 'invoice' ? handleExtractInvoice : handleExtractSpreadsheet}
            className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-sm"
          >
            {step === 'extracting' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{activeTab === 'spreadsheet' ? 'Analyzing Spreadsheet...' : 'Extracting with AI...'}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>{activeTab === 'spreadsheet' ? 'Import & Map Spreadsheet' : 'Extract Invoice Data'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // ── Review Step ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 text-xs max-h-[78vh] overflow-y-auto pr-1">
      {/* Header Banner */}
      <div className="flex items-center justify-between p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-800">
        <div className="flex items-center space-x-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="text-[11px] font-semibold">
            Extracted purchase data ready for review. <strong>Edit all fields before confirming into draft.</strong>
          </span>
        </div>
        {extractionMethod && (
          <span className="px-2 py-0.5 bg-green-100 text-green-800 border border-green-300 rounded text-[10px] font-mono">
            {extractionMethod === 'canonical_deterministic'
              ? 'Canonical Template'
              : extractionMethod === 'heuristic_deterministic'
              ? 'Header Heuristics'
              : extractionMethod === 'ai_gemini'
              ? 'Gemini AI Mapped'
              : 'Invoice Extracted'}
          </span>
        )}
      </div>

      {/* Warnings List */}
      {spreadsheetWarnings.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-900 space-y-1">
          <div className="flex items-center space-x-1.5 font-bold">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <span>Extraction Warnings & Integrity Checks:</span>
          </div>
          <ul className="list-disc list-inside space-y-0.5 text-[10px] pl-1">
            {spreadsheetWarnings.map((w, idx) => (
              <li key={idx}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Supplier & Header Information */}
      <div className="p-3 bg-brand-bg border border-brand-border rounded-lg space-y-3">
        <h4 className="font-bold text-brand-black flex items-center space-x-1.5">
          <Edit2 className="w-3.5 h-3.5 text-brand-green" />
          <span>Purchase Header Information</span>
        </h4>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Supplier Name</label>
            <input
              type="text"
              value={editSupplierName}
              onChange={(e) => setEditSupplierName(e.target.value)}
              placeholder="e.g. Dubai Wholesale"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Invoice / Reference #</label>
            <input
              type="text"
              value={editReference}
              onChange={(e) => setEditReference(e.target.value)}
              placeholder="e.g. INV-2026-001"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Purchase Date</label>
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Shipping Cost (PKR)</label>
            <input
              type="number"
              min="0"
              value={editShipping}
              onChange={(e) => setEditShipping(Math.max(0, Number(e.target.value)))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Other Cost (PKR)</label>
            <input
              type="number"
              min="0"
              value={editOther}
              onChange={(e) => setEditOther(Math.max(0, Number(e.target.value)))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Discount (PKR)</label>
            <input
              type="number"
              min="0"
              value={editDiscount}
              onChange={(e) => setEditDiscount(Math.max(0, Number(e.target.value)))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Amount Paid (PKR)</label>
            <input
              type="number"
              min="0"
              value={editAmountPaid}
              onChange={(e) => setEditAmountPaid(Math.max(0, Number(e.target.value)))}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Payment Method</label>
            <select
              value={editPaymentMethod}
              onChange={(e) => setEditPaymentMethod(e.target.value as any)}
              className={inputClass}
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="mobile_wallet">Mobile Wallet</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-span-2 sm:col-span-3">
            <label className="block text-[10px] font-semibold text-brand-muted mb-0.5">Notes</label>
            <input
              type="text"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Optional purchase order / batch notes"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {extractError && <ErrorAlert message={extractError} />}

      {/* Extracted Line Items */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-brand-black flex items-center space-x-1.5">
            <Layers className="w-3.5 h-3.5 text-brand-green" />
            <span>{reviewModels.length} {spreadsheetDraft ? 'model / item groups' : 'items'} · {editItems.reduce((sum, item) => sum + Number(item.quantity), 0)} total units</span>
          </h4>
          <button
            type="button"
            onClick={addItem}
            className="flex items-center space-x-1 px-2 py-1 text-xs text-brand-green border border-brand-green rounded hover:bg-green-50 font-medium"
          >
            <Plus className="w-3 h-3" />
            <span>Add Item</span>
          </button>
        </div>

        {spreadsheetDraft && (
          <p className="text-[11px] text-brand-muted">
            Open a laptop model to see its different configurations. RAM, storage, quantity and cost edits apply only to that configuration. View units keeps every serial number available.
          </p>
        )}
        {missingSpecsGroups.length > 0 && (
          <div role="status" className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
            <strong>{missingSpecsGroups.length} laptop {missingSpecsGroups.length === 1 ? 'configuration needs' : 'configurations need'} RAM or SSD / storage.</strong>
            <p className="mt-1">Open the models marked Needs specifications and complete their missing fields. To change one unit, use View units → Edit separately.</p>
          </div>
        )}

        {reviewModels.map((modelGroup) => (
          <details key={modelGroup.id} className="border border-brand-border rounded-lg overflow-hidden"
            open={modelGroup.kind !== 'laptop' || modelGroup.variants.some(({ item }) => !item.draftItem || item.draftItem.item_type === 'unknown') ? true : undefined}>
            <summary className="cursor-pointer p-3 bg-brand-bg text-brand-black marker:text-brand-green">
              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 align-middle">
                <strong className="text-sm">{modelGroup.label}</strong>
                <span className="rounded bg-white border border-brand-border px-2 py-0.5 font-semibold">{modelGroup.quantity} units</span>
                {modelGroup.kind === 'laptop' && <span className="text-brand-muted">{modelGroup.variants.length} {modelGroup.variants.length === 1 ? 'configuration' : 'configurations'}</span>}
                <span className="font-mono font-semibold">{formatCurrency(modelGroup.total)}</span>
                {spreadsheetDraft && modelGroup.missingSpecsCount > 0 && <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900 font-semibold">Needs specifications · {modelGroup.missingSpecsCount}</span>}
              </span>
            </summary>
            {modelGroup.kind === 'laptop' && <p className="px-3 pt-3 text-[11px] text-brand-muted">Different specifications stay below this model. Each configuration has its own quantity, unit cost and serial numbers.</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-xs border-collapse">
            <thead className="bg-brand-bg text-[11px] text-brand-muted font-semibold border-b border-brand-border">
              <tr>
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">{modelGroup.kind === 'laptop' ? 'Configuration / Specifications' : 'Description'}</th>
                <th className="px-3 py-2 text-right w-20">Qty</th>
                <th className="px-3 py-2 text-right w-28">Unit Cost (PKR)</th>
                <th className="px-3 py-2 text-right w-28">Line Total</th>
                <th className="px-2 py-2 w-10 text-center"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {modelGroup.variants.map((group, idx) => {
                const item = group.item;
                const specs = getReviewLaptopSpecs(item);
                const laptop = Boolean(spreadsheetDraft) && isReviewLaptop(item);
                const lineTotal = group.quantity * (Number(item.unit_cost) || 0);
                const warnings = [...new Set(group.members.flatMap(member => [
                  ...(member.draftItem?.warnings || []), member.draftItem?.duplicate_warning || '',
                ]).filter(Boolean))];
                return (
                  <React.Fragment key={group.id}>
                  <tr className="hover:bg-brand-bg/50 align-top">
                    <td className="px-3 py-3 text-brand-muted font-mono text-[10px]">{idx + 1}</td>
                    <td className="px-3 py-2 min-w-[300px]">
                      <input
                        type="text"
                        aria-label={`Description for ${modelGroup.label}, configuration ${idx + 1}`}
                        value={item.description}
                        onChange={(e) => updateGroup(group, 'description', e.target.value)}
                        placeholder="e.g. HP EliteBook 840 G8 i7 16GB 512GB"
                        className={inputClass}
                      />
                      {laptop && <p className="mt-1 text-[10px] text-brand-muted">
                        {[item.draftItem?.cpu, item.draftItem?.gpu, item.draftItem?.screen_size,
                          item.draftItem?.condition, item.draftItem?.grade].filter(Boolean).join(' · ')}
                      </p>}
                      {spreadsheetDraft && (!item.draftItem || item.draftItem.item_type === 'unknown') && (
                        <div className="mt-1 space-y-1">
                          <select
                            aria-label="Item type"
                            value={item.manualType || ''}
                            onChange={(e) => updateManualItem(item.id, { manualType: e.target.value as 'laptop' | 'accessory' | '' })}
                            className={inputClass}
                          >
                            <option value="">Choose item type</option>
                            <option value="laptop">Laptop</option>
                            <option value="accessory">Accessory</option>
                          </select>
                          {item.manualType === 'laptop' && (
                            <div className="flex gap-1">
                              <input aria-label="Laptop brand" placeholder="Brand" value={item.manualBrand || ''}
                                onChange={(e) => updateManualItem(item.id, { manualBrand: e.target.value })} className={inputClass} />
                              <input aria-label="Laptop model" placeholder="Model" value={item.manualModel || ''}
                                onChange={(e) => updateManualItem(item.id, { manualModel: e.target.value })} className={inputClass} />
                            </div>
                          )}
                        </div>
                      )}
                      {laptop && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <div>
                            <label htmlFor={`ram-${group.id}`} className="block text-[11px] font-semibold mb-1">
                              RAM {isMissingLaptopSpec(specs.ram) && <span className="text-amber-700">· Required</span>}
                            </label>
                            <input id={`ram-${group.id}`} value={specs.ram} placeholder="e.g. 16GB DDR4"
                              aria-required="true" aria-invalid={isMissingLaptopSpec(specs.ram)}
                              onChange={(e) => updateGroup(group, 'ram', e.target.value)}
                              className={`${inputClass} ${isMissingLaptopSpec(specs.ram) ? 'border-amber-400 bg-amber-50' : ''}`} />
                            <button type="button" className="mt-1 text-[10px] text-brand-green underline"
                              onClick={() => updateGroup(group, 'ram', 'No RAM installed')}>No RAM installed</button>
                          </div>
                          <div>
                            <label htmlFor={`storage-${group.id}`} className="block text-[11px] font-semibold mb-1">
                              SSD / storage {isMissingLaptopSpec(specs.storage) && <span className="text-amber-700">· Required</span>}
                            </label>
                            <input id={`storage-${group.id}`} value={specs.storage} placeholder="e.g. 512GB SSD"
                              aria-required="true" aria-invalid={isMissingLaptopSpec(specs.storage)}
                              onChange={(e) => updateGroup(group, 'storage', e.target.value)}
                              className={`${inputClass} ${isMissingLaptopSpec(specs.storage) ? 'border-amber-400 bg-amber-50' : ''}`} />
                            <button type="button" className="mt-1 text-[10px] text-brand-green underline"
                              onClick={() => updateGroup(group, 'storage', 'No drive installed')}>No drive installed</button>
                          </div>
                          {group.quantity > 1 && <p className="col-span-2 text-[10px] text-brand-muted">These specifications apply to the {group.quantity} units in this configuration only.</p>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        aria-label={`Quantity for ${modelGroup.label}, configuration ${idx + 1}`}
                        key={`qty-${group.id}-${group.quantity}`}
                        defaultValue={group.quantity}
                        onBlur={(e) => {
                          const quantity = spreadsheetDraft ? Number(e.target.value) : Math.max(1, parseInt(e.target.value) || 1);
                          if (!updateGroup(group, 'quantity', quantity)) e.currentTarget.value = String(group.quantity);
                        }}
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min="0"
                        aria-label={`Unit cost for ${modelGroup.label}, configuration ${idx + 1}`}
                        value={item.unit_cost}
                        onChange={(e) => updateGroup(group, 'unit_cost', spreadsheetDraft ? Number(e.target.value) : Math.max(0, Number(e.target.value)))}
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-brand-black">
                      {formatCurrency(lineTotal)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeGroup(group)}
                        className="p-1 text-brand-muted hover:text-red-600 rounded"
                        title="Remove this configuration and its units"
                        aria-label={`Remove ${modelGroup.label}, configuration ${idx + 1}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  {spreadsheetDraft && (
                    <tr>
                      <td colSpan={6} className="px-3 py-2 bg-brand-bg/40">
                        <details>
                          <summary className="cursor-pointer text-brand-green font-semibold">
                            View units ({group.quantity}){warnings.length > 0 ? ` · ${warnings.length} warnings` : ''}
                          </summary>
                          <div className="mt-2 max-h-60 overflow-y-auto space-y-2">
                            {warnings.length > 0 && <ul className="p-2 bg-amber-50 text-amber-900 rounded list-disc list-inside">
                              {warnings.map(warning => <li key={warning}>{warning}</li>)}
                            </ul>}
                            {group.members.map((member, unitIndex) => (
                              <div key={member.id} className="flex flex-wrap items-center justify-between gap-2 p-2 border border-brand-border bg-white rounded">
                                <div className="space-y-0.5 text-[11px]">
                                  <p className="font-semibold">Row {unitIndex + 1} · Quantity {member.quantity}</p>
                                  <p className="text-brand-muted">{member.draftItem?.source_sheet || 'Manual'}{member.draftItem?.source_row ? ` · source row ${member.draftItem.source_row}` : ''}</p>
                                  {laptop && <p>Serial: {member.draftItem?.serial_number || 'Not detected'} · Tag: {member.draftItem?.service_tag || 'Not detected'} · Barcode: {member.draftItem?.barcode || 'Not detected'}</p>}
                                  {member.quantity > 1 && laptop && <p className="text-amber-800">Additional units will need their own serial numbers in the purchase draft.</p>}
                                </div>
                                <div className="flex gap-3 shrink-0">
                                  {group.members.length > 1 && <button type="button" onClick={() => separateItem(member.id)} className="text-brand-green underline">Edit separately</button>}
                                  <button type="button" onClick={() => removeItem(member.id)} className="text-red-700 underline">Remove row</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
          </details>
        ))}
        {editItems.length === 0 && <p className="px-4 py-6 text-center text-brand-muted">No items in review draft. Click "Add Item" above to add items.</p>}
      </div>

      {/* Financial Summary & Landed Cost Preview */}
      <div className="p-3 bg-brand-bg border border-brand-border rounded-lg grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div>
          <span className="text-[10px] text-brand-muted block">Items Subtotal</span>
          <span className="font-bold text-brand-black font-mono text-sm">{formatCurrency(subtotal)}</span>
        </div>
        <div>
          <span className="text-[10px] text-brand-muted block">Shipping + Other Cost</span>
          <span className="font-bold text-brand-black font-mono text-sm">+{formatCurrency(extraCosts)}</span>
        </div>
        <div>
          <span className="text-[10px] text-brand-muted block">Discount</span>
          <span className="font-bold text-emerald-600 font-mono text-sm">-{formatCurrency(editDiscount)}</span>
        </div>
        <div>
          <span className="text-[10px] text-brand-muted block">Net Purchase Total</span>
          <span className="font-bold text-brand-green font-mono text-sm">{formatCurrency(total)}</span>
        </div>
      </div>

      {/* Modal Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-brand-border">
        <button
          type="button"
          onClick={() => {
            setStep('upload');
            setSelectedFile(null);
          }}
          className="px-3 py-2 text-brand-muted hover:text-brand-black font-medium text-xs"
        >
          ← Choose Different File
        </button>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-brand-muted hover:text-brand-black font-medium text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={missingSpecsGroups.length > 0}
            className="inline-flex items-center space-x-1.5 px-5 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover shadow-sm text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Confirm into Receive Purchase Draft</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
